import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { listCurrentUserLicenses, getActivationsForLicense, toggleLicenseStatus, deactivateActivation, reactivateActivation } from '@/services/licenses';
import { getOrderById } from '@/services/orders';
import type { License, Order } from '@/domain/types';
import type { LicenseActivation } from '@/services/licenses';
import { formatDate } from '@/lib/format';

export default function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [license, setLicense] = useState<License | null>(null);
  const [sites, setSites] = useState<LicenseActivation[]>([]);
  const [loading, setLoading] = useState(true);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);

  const [toggling, setToggling] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState('');
  const [siteError, setSiteError] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const allLicenses = await listCurrentUserLicenses();
        if (!mounted) return;
        const found = allLicenses.find((l) => l.id === id) ?? null;
        setLicense(found);
        if (found && id) {
          const [activationData, orderData] = await Promise.all([
            getActivationsForLicense(id),
            found.orderId ? getOrderById(found.orderId) : Promise.resolve(null),
          ]);
          if (mounted) { setSites(activationData); setOrder(orderData); }
        }
      } catch {
        if (mounted) setLicense(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
      </div>
    );
  }

  if (!license) {
    return (
      <div className="text-center py-16">
        <i className="ri-error-warning-line text-4xl text-surface-300 mb-3" />
        <p className="text-surface-500">License not found</p>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-surface-100 text-surface-600',
    expired: 'bg-amber-50 text-amber-700',
    disabled: 'bg-red-50 text-red-700',
    refunded: 'bg-red-50 text-red-700',
  };

  const isPaid = order?.status === 'paid';
  const fullLicenseKey = license.licenseKey ?? `****-****-****-${license.licenseKeyLast4}`;

  const handleReveal = () => {
    if (!isPaid) return;
    setRevealedKey(fullLicenseKey);
  };

  const handleToggle = async () => {
    if (!license || toggling) return;
    setToggling(true);
    setToggleError('');
    try {
      await toggleLicenseStatus(license.id);
      const allLicenses = await listCurrentUserLicenses();
      const updated = allLicenses.find((l) => l.id === license.id) ?? null;
      if (updated) setLicense(updated);
    } catch (err: unknown) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update license.');
    } finally {
      setToggling(false);
    }
  };

  const handleDeactivateSite = async (siteId: string) => {
    if (deactivating) return;
    setDeactivating(siteId);
    setSiteError('');
    try {
      await deactivateActivation(siteId);
      setSites((prev) =>
        prev.map((s) => (s.id === siteId ? { ...s, status: 'deactivated' as const } : s)),
      );
    } catch {
      setSiteError('Failed to deactivate site.');
    } finally {
      setDeactivating(null);
    }
  };

  const handleReactivateSite = async (siteId: string) => {
    if (deactivating) return;
    setDeactivating(siteId);
    setSiteError('');
    try {
      await reactivateActivation(siteId);
      setSites((prev) =>
        prev.map((s) => (s.id === siteId ? { ...s, status: 'active' as const } : s)),
      );
    } catch (err: unknown) {
      setSiteError(err instanceof Error ? err.message : 'Failed to reactivate site.');
    } finally {
      setDeactivating(null);
    }
  };

  const handleCopy = async () => {
    const keyToCopy = revealedKey || fullLicenseKey;
    try {
      await navigator.clipboard.writeText(keyToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = keyToCopy;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm text-surface-500 mb-2">
        <Link to="/dashboard/licenses" className="hover:text-brand-600 transition-colors">Licenses</Link>
        <i className="ri-arrow-right-s-line text-xs" />
        <span className="text-surface-900 font-medium">{license.productName}</span>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-surface-900 mb-1">{license.productName}</h1>
            <p className="text-sm text-surface-500">{license.planName} Plan</p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColors[license.status] || 'bg-surface-100 text-surface-600'}`}>
              {license.status}
            </span>
            {license.status !== 'expired' && license.status !== 'disabled' && license.status !== 'refunded' && (
              <button
                onClick={handleToggle}
                disabled={toggling}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  license.status === 'active'
                    ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                }`}>
                {toggling ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : license.status === 'active' ? (
                  'Deactivate'
                ) : (
                  'Activate'
                )}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="p-3 rounded-lg bg-surface-50 border border-surface-100">
            <p className="text-xs text-surface-500 mb-1">License Key</p>
            <p className="text-sm font-mono text-surface-900">****-****-****-{license.licenseKeyLast4}</p>
          </div>
          <div className="p-3 rounded-lg bg-surface-50 border border-surface-100">
            <p className="text-xs text-surface-500 mb-1">Site Limit</p>
            <p className="text-sm font-semibold text-surface-900">{license.siteLimit === 999 ? 'Unlimited' : license.siteLimit} sites</p>
          </div>
          <div className="p-3 rounded-lg bg-surface-50 border border-surface-100">
            <p className="text-xs text-surface-500 mb-1">Created</p>
            <p className="text-sm font-semibold text-surface-900">{formatDate(license.createdAt)}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-surface-500">Sites used: {license.activatedSites} / {license.siteLimit === 999 ? 'Unlimited' : license.siteLimit}</span>
        </div>
        <div className="h-2 bg-surface-200 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-brand-500 rounded-full"
            style={{ width: license.siteLimit === 999 ? '5%' : `${(license.activatedSites / license.siteLimit) * 100}%` }}
          />
        </div>

        {toggleError && (
          <p className="text-xs text-red-600 flex items-center gap-1 mb-4">
            <i className="ri-error-warning-line" /> {toggleError}
          </p>
        )}

        {siteError && (
          <p className="text-xs text-red-600 flex items-center gap-1 mb-4">
            <i className="ri-error-warning-line" /> {siteError}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleReveal}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition-colors whitespace-nowrap"
          >
            {isPaid ? <i className="ri-eye-line mr-1" /> : <i className="ri-lock-line mr-1" />}
            {isPaid ? 'Reveal Key' : 'Pay to Unlock'}
          </button>
          {isPaid && (
            <button
              onClick={handleCopy}
              className="px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} mr-1`} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </div>

      {/* Active Websites */}
      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-200">
          <h2 className="font-semibold text-surface-900 text-sm">Active Websites</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Domain</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Plugin Version</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">WP / PHP</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Activated</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Last Check</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3 text-surface-700 font-medium">{site.siteHost}</td>
                  <td className="px-5 py-3 text-surface-600">v{site.pluginVersion}</td>
                  <td className="px-5 py-3 text-surface-500 text-xs">WP {site.wpVersion} / PHP {site.phpVersion}</td>
                  <td className="px-5 py-3 text-surface-500">{formatDate(site.activatedAt)}</td>
                  <td className="px-5 py-3 text-surface-500">{formatDate(site.lastCheckAt)}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        site.status === 'active'
                          ? 'bg-green-50 text-green-700'
                          : site.status === 'deactivated'
                          ? 'bg-surface-100 text-surface-600'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {site.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {site.status === 'active' ? (
                        <button
                          onClick={() => handleDeactivateSite(site.id)}
                          disabled={deactivating === site.id}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-surface-500 hover:text-red-600 transition-colors"
                          title="Deactivate">
                          {deactivating === site.id ? (
                            <i className="ri-loader-4-line animate-spin text-xs" />
                          ) : (
                            <i className="ri-close-circle-line" />
                          )}
                        </button>
                      ) : site.status === 'deactivated' ? (
                        <button
                          onClick={() => handleReactivateSite(site.id)}
                          disabled={deactivating === site.id}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-100 text-surface-500 hover:text-green-600 transition-colors"
                          title="Reactivate">
                          {deactivating === site.id ? (
                            <i className="ri-loader-4-line animate-spin text-xs" />
                          ) : (
                            <i className="ri-refresh-line" />
                          )}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {sites.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-surface-500">
                    No websites activated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unpaid notice */}
      {!isPaid && order && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <i className="ri-alert-line text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-1">Payment required</p>
            <p className="text-xs text-amber-700 leading-relaxed mb-2">
              This license is inactive because order #{order.orderRef} hasn't been paid yet.
            </p>
            <Link to="/dashboard/orders" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 underline hover:text-amber-900">
              View your orders <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      )}

      {/* Reveal Key Modal */}
      {revealedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setRevealedKey(null)}
          />
          <div className="relative bg-white rounded-xl border border-surface-200 shadow-xl w-full max-w-md flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
              <div>
                <h3 className="font-semibold text-surface-900 text-sm">License Key Revealed</h3>
                <p className="text-xs text-surface-500 mt-0.5">
                  {license.productName} — {license.planName} Plan
                </p>
              </div>
              <button
                onClick={() => setRevealedKey(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <i className="ri-alert-line text-amber-600 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  For security, copy your license key now. Store it safely — you will only see the full key again by clicking "Reveal Key."
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1.5">Your License Key</p>
                <div className="bg-surface-900 rounded-lg p-4 flex items-center justify-between gap-3">
                  <code className="text-sm font-mono text-green-400 tracking-wider">
                    {revealedKey}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="shrink-0 px-3 py-1.5 rounded-md bg-surface-800 hover:bg-surface-700 text-green-400 text-xs font-medium transition-colors flex items-center gap-1.5"
                  >
                    <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'}`} />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-200 flex justify-end">
              <button
                onClick={() => setRevealedKey(null)}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}